import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import * as ClinicActions from "actions/ClinicActions";
import AccessDenied from "components/common/AccessDenied";
import Button from "components/common/Button";
import ChooseHospital from "components/admin/elements/ChooseHospital";
import SortableDataTable from "components/common/SortableDataTable";
import Dropdown from "components/common/Dropdown";
import Modal from "components/common/Modal";
import SpinnerTakeover from "components/common/SpinnerTakeover";
import Tooltip from "components/common/Tooltip";
import { PermissionTypes, userHasPermission } from "utils/permissions/rolesPermissions";
import * as UserPermissions from "constants/UserPermissions";

function HospitalsTable({
    canAssignHospital = false,
    canViewClinicManagement = false,
    clinicId,
    hospitals = {},
    loadingHospitals = false,
    assignHospital,
    getClinicHospitals,
}) {
    const [selectedClinicForHospitalAssignment, setSelectedClinicForHospitalAssignment] = useState(false);
    const [selectedStatusFilter, setStatusFilter] = useState("All");
    const [selectedInvoiceFilter, setInvoiceFilter] = useState("All");

    useEffect(() => {
        if (canViewClinicManagement) {
            getClinicHospitals(clinicId);
        }
    }, [canViewClinicManagement, clinicId, getClinicHospitals]);

    if (!canViewClinicManagement) {
        return null;
    }

    const STATUS_FILTER_OPTIONS = [
        { name: "All", value: "All" },
        { name: "Assigned", value: "Assigned" },
        { name: "Unassigned", value: "Unassigned" },
    ];

    const INVOICE_FILTER_OPTIONS = [
        { name: "All", value: "All" },
        { name: "+1 Invoice", value: "+1" },
        { name: "0 Invoices", value: "0" },
    ];

    const filteredHospitals = Object.values(hospitals).filter((hospital) => {
        if (selectedStatusFilter === "Assigned" && !hospital.canUnassign) {
            return false;
        }
        if (selectedStatusFilter === "Unassigned" && !hospital.canAssign) {
            return false;
        }

        if (selectedInvoiceFilter === "+1" && hospital.invoiceCount <= 0) {
            return false;
        }
        if (selectedInvoiceFilter === "0" && hospital.invoiceCount > 0) {
            return false;
        }

        return true;
    });

    const handleClearFilters = () => {
        setStatusFilter("All");
        setInvoiceFilter("All");
    };

    const handleAssignHospital = ({ id, name }) => {
        const newClinicForHospitalAssignment = {
            ...selectedClinicForHospitalAssignment,
            targetClinicId: id,
            targetClinicName: name,
            canAssign: false,
            canUnassign: true,
        };
        assignHospital(newClinicForHospitalAssignment);
        setSelectedClinicForHospitalAssignment(null);
    };

    const handleUnassignHospital = (hospital) => {
        const updatedHospital = {
            ...hospital,
            targetClinicId: hospital.gatewayClinicId,
            targetClinicName: hospital.gatewayClinicName,
            canAssign: true,
            canUnassign: false,
        };
        assignHospital(updatedHospital);
    };

    const ASSIGN_COLUMNS = canAssignHospital
        ? [
              {
                  name: "",
                  selector: "assign",
                  key: "assign",
                  sortable: true,
                  sortValue: (row) => row.canAssign,
                  format: (row) => (
                      <div className="text-center">
                          {row.canAssign && (
                              <Button
                                  onClick={() => setSelectedClinicForHospitalAssignment(row)}
                                  small
                              >
                                  Assign
                              </Button>
                          )}
                          {row.canUnassign && (
                              <Button
                                  onClick={() => handleUnassignHospital(row)}
                                  small
                              >
                                  Unassign
                              </Button>
                          )}
                          {!(row.canAssign || row.canUnassign) && "Data Cannot be Moved"}
                      </div>
                  ),
              },
          ]
        : [];

    const HOSPITAL_COLUMNS = [
        {
            name: (
                <div className="text-center">
                    <div>Hospital ID</div>
                    <div>(Remote)</div>
                </div>
            ),
            selector: "remoteClinicHospitalID",
            key: "remoteClinicHospitalID",
            sortable: true,
            format: (row) => <div className="text-center">{row.remoteClinicHospitalID}</div>,
        },
        {
            name: (
                <div className="text-center">
                    <div>Hospital ID</div>
                    <div>(Greenline)</div>
                </div>
            ),
            selector: "hospitalId",
            key: "hospitalId",
            sortable: true,
            format: (row) => <div className="text-center">{row.hospitalId}</div>,
        },
        {
            name: "Hospital Name",
            selector: "hospitalName",
            key: "hospitalName",
            sortable: true,
            format: (row) => <div className="nowrap">{row.hospitalName}</div>,
        },
        {
            name: "Identifier",
            selector: "identifier",
            key: "identifier",
            format: (row) => <div className="nowrap">{row.identifier}</div>,
        },
        {
            name: <div className="text-center">Invoices</div>,
            selector: "invoiceCount",
            key: "invoiceCount",
            sortable: true,
            format: (row) => <div className="text-center">{row.invoiceCount}</div>,
        },
        {
            name: (
                <div className="text-center">
                    <div>Clinic ID</div>
                    <div>(Assigned To)</div>
                </div>
            ),
            selector: "gatewayClinicId",
            key: "gatewayClinicId",
            format: (row) => (
                <div className="text-center">
                    {row.gatewayClinicId === row.targetClinicId ? row.gatewayClinicId : row.targetClinicId}
                </div>
            ),
        },
        {
            name: "Hospital ID (Assigned To)",
            selector: "gatewayClinicName",
            key: "gatewayClinicName",
            sortable: true,
            sortValue: (row) => row.targetClinicName,
            format: (row) =>
                row.gatewayClinicId === row.targetClinicId ? (
                    <Tooltip position="left" tip="Gateway Clinic">
                        <div className="no-wrap">
                            <i className="fas fa-hospitals" /> {row.targetClinicName}
                        </div>
                    </Tooltip>
                ) : (
                    <div className="no-wrap">{row.targetClinicName}</div>
                ),
        },
        ...ASSIGN_COLUMNS,
    ];

    const hasHospitals = !!hospitals && Object.keys(hospitals).length;

    return (
        <div className="card" style={{ gridColumn: "1 / -1" }}>
            <div className="flex spaced-content align-bottom margin-bottom-sm">
                <div className="flex-none" style={{ width: "250px" }}>
                    <Dropdown
                        options={STATUS_FILTER_OPTIONS}
                        name="assignFilter"
                        value={selectedStatusFilter}
                        label="Filter by Assignment Status"
                        onChange={({ value }) => setStatusFilter(value)}
                        placeholder="Select Status"
                    />
                </div>
                <div className="flex-none" style={{ width: "250px" }}>
                    <Dropdown
                        options={INVOICE_FILTER_OPTIONS}
                        name="invoiceFilter"
                        value={selectedInvoiceFilter}
                        label="Filter by Invoice"
                        onChange={({ value }) => setInvoiceFilter(value)}
                        placeholder="Select Invoice Count"
                    />
                </div>
                <Button onClick={handleClearFilters} small className="btn-clear-filters">
                    Clear Filters
                </Button>
            </div>
            {hasHospitals ? (
                <SortableDataTable
                    green
                    title="Clinics"
                    columns={HOSPITAL_COLUMNS}
                    rawData={filteredHospitals}
                    highlightOnHover
                    striped
                />
            ) : (
                <div>
                    <h3>Clinics</h3>
                    <div>Nothing to show</div>
                </div>
            )}
            <Modal
                show={!!selectedClinicForHospitalAssignment}
                onClose={() => setSelectedClinicForHospitalAssignment(null)}
                mediumSmall
                modalTitle="Choose Clinic"
                modalSubTitle="Please choose a Non-Gateway Clinic from the list below"
            >
                {canAssignHospital ? (
                    <ChooseHospital onSelect={handleAssignHospital} />
                ) : (
                    <AccessDenied />
                )}
            </Modal>
            <SpinnerTakeover show={loadingHospitals} />
        </div>
    );
}

HospitalsTable.propTypes = {
    canAssignHospital: PropTypes.bool,
    canViewClinicManagement: PropTypes.bool,
    clinicId: PropTypes.number.isRequired,
    hospitals: PropTypes.object,
    loadingHospitals: PropTypes.bool,
    assignHospital: PropTypes.func.isRequired,
    getClinicHospitals: PropTypes.func.isRequired,
};

export default connect(
    (state, ownProps) => {
        const userProfile = state.user.userProfile;
        const hospitals = state.entities.hospitals.data || {};

        // Permissions
        const canAssignHospital = userHasPermission(
            PermissionTypes.ASSIGN_HOSPITAL,
            UserPermissions.CLINIC_MANAGEMENT,
            userProfile
        );
        const canViewClinicManagement = userHasPermission(
            PermissionTypes.VIEW,
            UserPermissions.CLINIC_MANAGEMENT,
            userProfile
        );

        return {
            canAssignHospital,
            canViewClinicManagement,
            hospitals: hospitals[ownProps.clinicId] || {},
            loadingHospitals: state.entities.hospitals.loading || false,
        };
    },
    (dispatch) => ({
        assignHospital: (data) => dispatch(ClinicActions.assignHospital(data)),
        getClinicHospitals: (clinicId) => dispatch(ClinicActions.getClinicHospitals(clinicId)),
    })
)(HospitalsTable);